import { isPastDueDate } from '../../../../shared/utils/overdue';
import { IN_PROGRESS_URGENCY_META } from './inProgress.constants';

export function getUrgencyBadgeClass(urgency) {
  return (
    IN_PROGRESS_URGENCY_META[urgency]?.badgeClass ||
    'border-[var(--border)] text-[var(--text-primary)] bg-[var(--surface-raised)]'
  );
}

export function getInProgressStageCountLabel(count) {
  return `${count} ${count === 1 ? 'affare' : 'affari'}`;
}

export function parseDealValue(value) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  const parsed = Number(
    String(value || '')
      .replace(/[€\s]/g, '')
      .replace(/\./g, '')
      .replace(',', '.')
  );
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseDateValue(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function getInProgressDueDate(deal = {}) {
  const directDates = [
    deal.paymentExpectedDate,
    deal.paymentDate,
    deal.dataPagamento,
    ...(deal.installmentDueDates || []),
    ...(deal.dilazionamento?.scadenze || []),
  ].map(parseDateValue).filter(Boolean);
  const now = new Date();
  const upcoming = directDates
    .filter((date) => date.getTime() >= new Date(now.toDateString()).getTime())
    .sort((left, right) => left.getTime() - right.getTime())[0];
  return upcoming || directDates.sort((left, right) => left.getTime() - right.getTime())[0] || null;
}

export function isInProgressDealOverdue(deal = {}) {
  if (['annullato', 'annullata', 'completato', 'completata', 'completed', 'cancelled'].includes(deal.status)) return false;
  // Any expired installment counts, even when another installment is still in the future.
  return [deal.paymentExpectedDate, deal.paymentDate, deal.dataPagamento,
    ...(deal.installmentDueDates || []), ...(deal.dilazionamento?.scadenze || [])]
    .some((date) => isPastDueDate(date));
}

export function filterInProgressByOverview(deals, overviewFilter) {
  if (!overviewFilter || overviewFilter === 'total') return deals;
  if (overviewFilter === 'estimatedVolume') {
    return deals.filter((deal) => parseDealValue(deal.value) > 0);
  }
  if (overviewFilter === 'highPriority') {
    return deals.filter((deal) => deal.urgency === 'alta');
  }
  if (overviewFilter === 'overdue') {
    return deals.filter(isInProgressDealOverdue);
  }
  return deals;
}

export function calculateInProgressStats(deals = [], stages = []) {
  const activeStages = stages.filter((stage) => deals.some((deal) => deal.stage === stage)).length;
  const totalValue = deals.reduce((total, deal) => total + parseDealValue(deal.value), 0);
  const assignedCount = deals.filter((deal) => deal.staffId || deal.assignee).length;
  const highPriority = deals.filter((deal) => deal.urgency === 'alta').length;
  const overdue = deals.filter(isInProgressDealOverdue).length;

  return {
    total: deals.length,
    activeStages,
    totalValue,
    estimatedVolume: totalValue,
    assignedCount,
    highPriority,
    overdue,
  };
}

export function createInProgressDealPayload(deal, existingDeals = []) {
  const nextId =
    Math.max(...existingDeals.map((item) => Number(item.id) || 0), 0) + 1;

  return {
    ...deal,
    id: deal.id || nextId,
  };
}

export function createInProgressDealState(existingDeals = [], deal) {
  const dealPayload = createInProgressDealPayload(deal, existingDeals);

  return {
    deals: [...existingDeals, dealPayload],
    deal: dealPayload,
  };
}

export function saveInProgressDealState(existingDeals = [], updatedDeal) {
  return {
    deals: existingDeals.map((deal) =>
      deal.id === updatedDeal.id ? updatedDeal : deal
    ),
    deal: updatedDeal,
  };
}

export function removeInProgressDealState(existingDeals = [], dealId) {
  return {
    deals: existingDeals.filter((deal) => deal.id !== dealId),
  };
}

export function updateInProgressDealStageState(existingDeals = [], dealId, newStage) {
  let updatedDeal = null;

  const deals = existingDeals.map((deal) => {
    if (deal.id !== dealId) return deal;

    updatedDeal = {
      ...deal,
      stage: newStage,
    };

    return updatedDeal;
  });

  return {
    deals,
    deal: updatedDeal,
  };
}

export function getInProgressMoveMessage(dealName, destinationType) {
  const destinationLabel =
    destinationType === 'proposta'
      ? 'Proposte'
      : destinationType === 'inCorso'
        ? 'Affari in Corso'
        : 'Affari Completati';

  return `Affare "${dealName}" spostato in ${destinationLabel}`;
}
